import { useNavigate } from "react-router-dom";
import IconMail from "../../../components/icons/icon-mail";
import { useRef, useState } from "react";

interface ForgotPasswordComponentProps {
  setCompanyName: (value: string) => void;
  setUsernameForgotPassword: (value: string) => void;
  setValueOtp: (otpCode: string) => void;
  toFormVerify: () => void;
  isLoading: boolean;
  isDisabled: boolean;
}

export const ForgotPasswordComponent = ({
  setCompanyName,
  setUsernameForgotPassword,
  setValueOtp,
  toFormVerify,
  isLoading,
  isDisabled,
}: ForgotPasswordComponentProps) => {

  const navigate = useNavigate();
  const [otpCode, setOtpCode] = useState<string[]>(Array(6).fill(""));
  const [isShowError, setIsShowError] = useState(false);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const [errorCompanyNameVisible, setErrorCompanyNameVisible] = useState(false);
  const [errorUserNameVisible, setErrorUserNameVisible] = useState(false);
  const [errorCompanyName, setErrorCompanyName] = useState("");
  const [errorUserName, setErrorUserName] = useState("");

  const handleSendOtp = () => {
    if (otpCode.includes("")) {
      setIsShowError(true);
      return;
    }
    setIsShowError(false);
    toFormVerify();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Enter") {
      handleSendOtp();
    }
    if (e.key === "Delete" || e.key === "Backspace") {
      if (index > 0 && !otpCode[index]) {
        inputsRef.current[index - 1]?.focus();
      }
      setOtpCode((prevOtpCode) => {
        const newOtpCode = [...prevOtpCode];
        newOtpCode[index] = "";
        setValueOtp(newOtpCode.join(""));
        return newOtpCode;
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { value } = e.target;
    if (/^\d*$/.test(value)) {
      setOtpCode((prevOtpCode) => {
        const newOtpCode = [...prevOtpCode];
        newOtpCode[index] = value;
        setValueOtp(newOtpCode.join(""));
        return newOtpCode;
      });
      if (value !== "" && index < inputsRef.current.length - 1) {
        inputsRef.current[index + 1]?.focus();
      }
      inputsRef.current.length === 6 && setIsShowError(false);
    }
  };

  function renderComponent({ content, icon, maxLength, htmlFor, autoFocus, isShowError, messageError, onKeyDown, onChange }: {
    content: string;
    maxLength?: number;
    htmlFor: string;
    autoFocus?: boolean;
    isShowError?: boolean;
    messageError?: string;
    icon: React.ReactNode;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }) {
    return (
      <div>
        <label
          htmlFor={htmlFor}
          className="block mb-2 text-sm font-medium text-gray-900"
        >
          {content}
        </label>
        <div className="relative">
          <span className="absolute -translate-y-1/2 start-4 top-1/2">
            {icon}
          </span>
          <input
            maxLength={maxLength}
            autoFocus={autoFocus}
            type="text"
            onKeyDown={onKeyDown}
            onChange={onChange}
            placeholder="Nhập thông tin"
            className="form-input ps-10 placeholder:text-white-dark"
          />
        </div>
        {isShowError && <span className="text-sm text-red-500">{messageError}</span>}
      </div>
    )
  }

  return (
    <>
      <div className="mb-5">
        <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-2xl ">
          Đặt lại mật khẩu
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          <span className="pr-2">Bạn đã nhớ mật khẩu?</span>
          <button
            type="button"
            data-testid="button-backward-login"
            className="font-medium text-primary decoration-2 hover:underline"
            onClick={() => navigate(-1)}
          >
            Đăng nhập
          </button>
        </p>
      </div>
      <form className="space-y-4" data-testid="forgot-password-form">
        {renderComponent({
          autoFocus: true,
          maxLength: 100,
          content: "Tên công ty",
          htmlFor: "company-forgot-password",
          icon: <IconMail fill={false} />,
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              toFormVerify();
            }
          },
          onChange: (e) => {
            const { value } = e.target;
            if (/^\s+$/.test(value)) { return }
            setCompanyName(value)
            setErrorCompanyNameVisible(value.length < 2);
            setErrorCompanyName(value.length < 2 ? "Độ dài tối thiểu 2 kí tự" : "");
          },
          isShowError: errorCompanyNameVisible,
          messageError: errorCompanyName,
        })}
        {renderComponent({
          content: "Tên đăng nhập",
          maxLength: 50,
          htmlFor: "name-forgot-password",
          icon: <IconMail fill={false} />,
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              handleSendOtp();
            }
          },
          onChange: (e) => {
            const { value } = e.target;
            if (/^\s+$/.test(value)) { return }
            setUsernameForgotPassword(value)
            setErrorUserNameVisible(value.length < 2);
            setErrorUserName(value.length < 2 ? "Độ dài tối thiểu 2 kí tự" : "");
          },
          isShowError: errorUserNameVisible,
          messageError: errorUserName,
        })}
        <div>
          <label
            htmlFor="otp-forgot-password"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Mã xác minh
          </label>
          <div className="flex items-center justify-center gap-3 xl:flex-nowrap md:flex-nowrap sm:flex-wrap max-[500px]:flex-wrap">
            {Array.from({ length: 6 }, (_, index) => (
              <input
                key={index}
                type="text"
                ref={(el) => (inputsRef.current[index] = el)}
                className="p-4 text-2xl font-extrabold text-center border border-transparent rounded outline-none appearance-none w-14 h-14 text-slate-900 bg-slate-100 hover:border-slate-200 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                maxLength={1}
                value={otpCode[index]}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onChange={(e) => handleChange(e, index)}
                data-testid={`input-otp-${index}`}
              />
            ))}
          </div>
          {isShowError && (
            <p className="text-center text-red-500">
              Vui lòng nhập đầy đủ mã xác minh
            </p>
          )}
        </div>
        <button
          type="button"
          className="btn btn-gradient w-full border-0 shadow-[0_10px_20px_-10px_rgba(67,97,238,0.44)]"
          onClick={toFormVerify}
          data-testid="button-forgot-password"
          disabled={isLoading || isDisabled}
        >
          {isLoading && (
            <span className="inline-block w-5 h-5 align-middle border-2 border-white rounded-full animate-spin border-l-transparent ltr:mr-4 rtl:ml-4"></span>
          )}
          ĐẶT LẠI MẬT KHẨU
        </button>
      </form>
    </>
  );
};
